import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { resolveAccess } from '../utils/access';
import '../styles/tmc-request-card.css';

const API = 'http://localhost:3001';
function withAuthHeaders(headers = {}) {
  const token = localStorage.getItem('authToken') || '';
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
}
async function fetchAuth(url, options = {}) {
  const opts = { ...options, headers: withAuthHeaders(options.headers || {}) };
  return fetch(url, opts);
}

function nextMonthYear(d = new Date()) {
  const m = d.getMonth(); // 0..11
  const y = d.getFullYear();
  const nm = (m + 1) % 12;
  const ny = y + (m === 11 ? 1 : 0);
  return { month: nm + 1, year: ny };
}

function money(n) { return (Number(n)||0).toLocaleString('ru-RU', { minimumFractionDigits:2, maximumFractionDigits:2 }); }

const FULL_MONTH_NAMES = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

const desiredDateFormatter = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });

function toInputDateString(value) {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDesiredDate(value) {
  const normalized = toInputDateString(value);
  if (!normalized) return '';
  const [year, month, day] = normalized.split('-').map(Number);
  if (!year || !month || !day) return normalized;
  const date = new Date(Date.UTC(year, month - 1, day));
  return desiredDateFormatter.format(date);
}

const formatQty = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : '—';
};

const EMPTY_REQUEST_ITEM = {
  tmc_id: null,
  name: '',
  unit: '',
  price: 0,
  supplier_link: '',
  group_id: null,
  card_qty: 0,
  request_qty: '',
  to_issue_qty: '',
};
const createEmptyRequestItem = () => ({ ...EMPTY_REQUEST_ITEM });

export default function TmcRequestCard({ isNew = false, isWriteOff = false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);
  const [cards, setCards] = React.useState([]);
  const [tmcDict, setTmcDict] = React.useState([]);
  const [draftItem, setDraftItem] = React.useState(() => createEmptyRequestItem());
  const draftRowRef = React.useRef(null);
  const [fillCalcLoading, setFillCalcLoading] = React.useState(false);
  const [fillPrevLoading, setFillPrevLoading] = React.useState(false);
  const [prevMonthChoice, setPrevMonthChoice] = React.useState({ open: false, requests: [] });
  const [selectedRowIdx, setSelectedRowIdx] = React.useState(null);
  const [itemFilters, setItemFilters] = React.useState({ name: '' });
  const [showOnlyNonZero, setShowOnlyNonZero] = React.useState(false);

  const routeIsWriteOff = React.useMemo(() => {
    if (isWriteOff) return true;
    return String(location.pathname || '').includes('/writeoff/');
  }, [isWriteOff, location.pathname]);

  const applyServerData = React.useCallback((payload) => {
    if (!payload) {
      setData(payload);
      return;
    }
    setData({
      ...payload,
      requestType: String(payload?.requestType || payload?.request_type || (routeIsWriteOff ? 'writeoff' : 'purchase')).toLowerCase() === 'writeoff' ? 'writeoff' : 'purchase',
      desiredDeliveryDate: toInputDateString(payload.desiredDeliveryDate),
    });
  }, [routeIsWriteOff, setData]);

  const user = React.useMemo(() => { try { return JSON.parse(localStorage.getItem('authUser')||'null') } catch { return null } }, []);
  const access = React.useMemo(() => resolveAccess(user), [user]);
  const isSuper = access.isSuper;
  const isAdmin = access.isAdmin;
  const isGd = access.hasGdRole;
  const canManagerStage = access.canSign.managerStage;
  const canAssistantStage = access.canSign.assistantStage;
  const statusValue = String(data?.status||'').trim();
  // Если статус уже продвинут дальше черновика, считаем менеджерскую стадию завершенной (на случай устаревших записей без managerSignedAt)
  const managerSigned = !!data?.managerSignedAt || ['in_progress','awaiting_gd','done'].includes(statusValue);
  const assistantSigned = !!data?.assistantSignedAt;
  const statusDone = statusValue === 'done' || (!!data?.gdSignedAt) || (!!data?.assistantSignedAt && !!data?.managerSignedAt);
  const readOnly = assistantSigned || statusDone;
  const awaitingGd = !!data?.gdApprovalAt && !data?.gdSignedAt;
  const assistantUnsignAllowed = React.useMemo(() => {
    if (!data?.assistantSignedAt) return false;
    const deadline = addWorkingDays(new Date(data.assistantSignedAt), 2);
    return new Date() <= deadline;
  }, [data?.assistantSignedAt]);
  const canAssistantUnsignNow = awaitingGd || (assistantSigned && assistantUnsignAllowed);

  function addWorkingDays(start, days) {
    const d = new Date(start);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        added += 1;
      }
    }
    return d;
  }

  const createEmpty = React.useCallback(() => {
    const { month, year } = nextMonthYear();
    const requestType = routeIsWriteOff ? 'writeoff' : 'purchase';
    setData({
      id: null,
      month,
      year,
      cardCode: '',
      objectName: '',
      requestType,
      desiredDeliveryDate: '',
      _cardInput: '',
      items: [],
      limitConsumables: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      managerSignedAt: null,
      managerSignedBy: null,
      assistantSignedAt: null,
      assistantSignedBy: null,
      comment: '',
    });
  }, [routeIsWriteOff]);

  const loadLists = React.useCallback(async () => {
    try {
      const [cardsRes, tmcRes] = await Promise.all([
        fetchAuth(`${API}/api/card-calc?page=1&perPage=400&full=1`),
        fetchAuth(`${API}/api/tmc`),
      ]);
      if (cardsRes?.ok) {
        const cardsPayload = await cardsRes.json();
        const arr = Array.isArray(cardsPayload)
          ? cardsPayload
          : Array.isArray(cardsPayload?.items)
            ? cardsPayload.items
            : [];
        const sorted = [...arr].sort((a, b) => Number(b.objectCode) - Number(a.objectCode));
        setCards(sorted);
      }
      if (tmcRes?.ok) {
        const dictPayload = await tmcRes.json();
        if (Array.isArray(dictPayload)) setTmcDict(dictPayload);
      }
    } catch (err) {
      console.error('load lists failed', err);
    }
  }, []);

  React.useEffect(() => { loadLists(); }, [loadLists]);

  const load = React.useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      if (isNew) {
        createEmpty();
      } else if (id) {
        const r = await fetchAuth(`${API}/api/tmc-requests/${id}`);
        if (!r.ok) throw new Error('load_failed');
        const v = await r.json();
        applyServerData(v);
      }
    } catch (e) {
      setError('Не удалось загрузить заявку');
    } finally {
      setIsLoading(false);
    }
  }, [id, isNew, createEmpty, applyServerData]);

  React.useEffect(() => { load(); }, [load]);

  // Автосохранение: после создания (когда есть id) сохраняем изменения полей автоматически с небольшой задержкой
  const saveTimer = React.useRef(null);
  const scheduleAutoSave = React.useCallback((draft = null, delay = 600) => {
    const snapshot = draft || data;
    if (!snapshot?.id) return; // только для существующих заявок
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const body = { ...snapshot };
        const r = await fetchAuth(`${API}/api/tmc-requests/${snapshot.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (r.ok) {
          const v = await r.json();
          applyServerData(v);
        }
      } catch (err) {
        console.error('autosave failed', err);
      }
    }, delay);
  }, [data, applyServerData]);

  React.useEffect(() => {
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, []);

  const loadWriteOffWarehouseItems = React.useCallback(async (card) => {
    const objectId = Number(card?.objectId || card?.object_id || card?.objectid || 0);
    if (!objectId) return [];
    try {
      const r = await fetchAuth(`${API}/api/warehouse/${encodeURIComponent(objectId)}`);
      if (!r.ok) return [];
      const payload = await r.json();
      const rows = Array.isArray(payload?.items) ? payload.items : [];
      return rows
        .filter((row) => !row?.writeOffDate && Number(row?.quantity || 0) > 0)
        .map((row) => ({
          tmc_id: row?.tmcId ?? row?.tmc_id ?? null,
          name: row?.name || '',
          unit: row?.unit || '',
          price: Number(row?.price) || 0,
          supplier_link: row?.supplierLink || row?.supplier_link || '',
          group_id: row?.groupId ?? row?.group_id ?? null,
          card_qty: Number(row?.quantity) || 0,
          request_qty: Number(row?.quantity) || 0,
          to_issue_qty: Number(row?.quantity) || 0,
        }));
    } catch (err) {
      console.error('load writeoff warehouse items failed', err);
      return [];
    }
  }, []);

  const pickCard = async (code) => {
    try {
      const r = await fetchAuth(`${API}/api/card-calc/${encodeURIComponent(code)}`);
      if (!r.ok) return;
      const card = await r.json();
      const tmc = Array.isArray(card?.sheets?.tmc) ? card.sheets.tmc : [];
      const limit = tmc.reduce((s, r) => s + (Number(r.total) || 0), 0);
      const fullCode = card.objectCodeFull || card.objectCode || code;
      let items = [];
      if (routeIsWriteOff) {
        items = await loadWriteOffWarehouseItems(card);
      } else {
        items = tmc.map(row => ({
          tmc_id: row.tmc_id ?? null,
          name: row.name || '',
          unit: row.unit || '',
          price: Number(row.price) || 0,
          supplier_link: row.supplier_link || '',
          group_id: null,
          card_qty: Number(row.qty) || 0,
          request_qty: 0,
          to_issue_qty: 0,
        }));
      }
      setData(prev => {
        const next = {
          ...(prev || {}),
          _cardInput: '',
          cardCode: fullCode,
          objectName: card.objectName || '',
          items,
          limitConsumables: Math.round(limit * 100) / 100,
        };
        scheduleAutoSave(next);
        return next;
      });
    } catch (err) {
      console.error('pick card failed', err);
    }
  };

  const saveDraft = async () => {
    try {
      setIsLoading(true); setError('');
      const body = { ...data, requestType: routeIsWriteOff ? 'writeoff' : 'purchase', desiredDeliveryDate: routeIsWriteOff ? null : (data?.desiredDeliveryDate || null) };
  if (!data.id) {
    const rawInput = data?._cardInput || data?.cardCode || '';
    const code = extractCardCode(rawInput);
    if (!/^\d{3,4}-\d+$/.test(code)) {
      setIsLoading(false); setError('Код карточки не распознан');
      alert('Сначала выберите актуализированную карточку расчёта (код вида 0001-23).');
      return;
    }
    const r = await fetchAuth(`${API}/api/tmc-requests`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ month: data.month, year: data.year, cardCode: code, comment: data.comment, requestType: routeIsWriteOff ? 'writeoff' : 'purchase', desiredDeliveryDate: routeIsWriteOff ? null : (data?.desiredDeliveryDate || null) }) });
        if (!r.ok) {
          const body = await r.json().catch(()=>({}));
          if (body?.error === 'card_not_found') {
            alert('Карточка расчёта не найдена по указанному коду. Проверьте, что выбран актуальный код вида 0001-23.');
          } else if (body?.error === 'forbidden_object') {
            alert('Нет прав на создание заявки по выбранному объекту.');
          } else if (body?.error === 'assistant_cannot_create_request') {
            alert('Помощник не может создавать заявку.');
          } else if (body?.error === 'month_year_card_required') {
            alert('Не заполнены месяц, год или код карточки расчёта.');
          } else {
            alert('Не удалось создать заявку (сервер).');
          }
          throw new Error('create_failed');
        }
        const created = await r.json();
        // patch items
    const r2 = await fetchAuth(`${API}/api/tmc-requests/${created.id}`, { method:'PATCH', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ items: data.items, limitConsumables: data.limitConsumables, objectName: data.objectName, comment: data.comment, desiredDeliveryDate: data?.desiredDeliveryDate || null }) });
        const upd = r2.ok ? await r2.json() : created;
        applyServerData(upd);
        nav(`/tmc-requests/${created.id}`, { replace: true });
      } else {
  const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}`, { method:'PATCH', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) {
          const body = await r.json().catch(()=>({}));
          if (body?.error === 'forbidden') alert('Нет прав для изменения этой заявки на текущей стадии.');
          else if (body?.error === 'already_fully_signed') alert('Заявка уже полностью подписана. Изменения недоступны.');
          else alert('Не удалось сохранить изменения.');
          throw new Error('save_failed');
        }
        const upd = await r.json();
        applyServerData(upd);
      }
    } catch (e) {
      setError('Не удалось сохранить');
    } finally {
      setIsLoading(false);
    }
  };

  const sign = async () => {
    if (!data?.id) return;
    try {
      setIsLoading(true); setError('');
      const stage = !managerSigned ? 'manager' : 'assistant';
      if (stage === 'assistant' && !isWriteOffRequest) {
        const items = Array.isArray(data?.items) ? data.items : [];
        const managerRows = items.filter((item) => {
          const requested = Number(item?.request_qty);
          return Number.isFinite(requested) && requested > 0;
        });
        const isIssueFilled = (value) => {
          const num = Number(value);
          if (!Number.isFinite(num)) return false;
          return num > 0;
        };
        const filledRows = managerRows.filter((item) => isIssueFilled(item?.to_issue_qty));
        const requiredCount = managerRows.length ? Math.ceil(managerRows.length * 0.3) : 0;
        if (managerRows.length && filledRows.length < requiredCount) {
          alert(`Заполните «К выдаче кол-во» минимум для ${requiredCount} из ${managerRows.length} строк (≥30%). Сейчас заполнено ${filledRows.length}.`);
          setIsLoading(false);
          return;
        }
      }
      const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/sign/${stage}`, { method:'POST' });
      if (!r.ok) {
        const body = await r.json().catch(()=>({}));
        if (body?.error === 'limit_exceeded') {
          const msg = `Превышение лимита расходных материалов: лимит ${money(body.limit)}, заказано ${money(body.requested)} (перерасход ${money(body.diff)}).`;
          alert(msg);
        } else if (body?.error === 'limit_exceeded_issue') {
          const msg = `Превышение лимита по выдаче: лимит ${money(body.limit)}, к выдаче ${money(body.toIssue)} (перерасход ${money(body.diff)}).`;
          alert(msg);
        } else if (body?.error === 'need_gd_approval') {
          const parts = [];
          if (typeof body.limit === 'number' && typeof body.toIssue === 'number' && body.toIssue > body.limit) {
            parts.push(`Расходники к выдаче: лимит ${money(body.limit)}, к выдаче ${money(body.toIssue)}`);
          }
          if (typeof body.orderedAmort === 'number' && typeof body.amortPerMonth === 'number' && body.orderedAmort > body.amortPerMonth) {
            parts.push(`Амортизируемые к заказу: ${money(body.orderedAmort)} > амортизация за месяц ${money(body.amortPerMonth)}`);
          }
          const want = window.confirm(`${parts.join('\n')}\n\nОтправить заявку на утверждение ГД?`);
          if (want) {
            const s = await fetchAuth(`${API}/api/tmc-requests/${data.id}/approval/gd`, { method:'POST' });
            if (s.ok) { const upd = await s.json(); applyServerData(upd); }
          }
          return;
        } else if (body?.error === 'issue_qty_ratio_low') {
          const required = Number(body?.required) || 0;
          const filled = Number(body?.filled) || 0;
          const total = Number(body?.total) || 0;
          alert(`Заполните «К выдаче кол-во» минимум для ${required} из ${total || 'N'} строк (≥30%). Сейчас заполнено ${filled}.`);
          return;
        } else {
          alert('Не удалось подписать');
        }
        return;
      }
      const upd = await r.json();
      applyServerData(upd);
    } catch (e) {
      setError('Не удалось подписать');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const onFocus = () => {
      fetchAuth(`${API}/api/tmc`).then(r=>r.json()).then(arr => setTmcDict(Array.isArray(arr)?arr:[])).catch(()=>{});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const updateItemQty = (idx, qty) => {
    setSelectedRowIdx(idx);
    setData(prev => {
      const items = (prev?.items||[]).map((it,i)=> i===idx ? { ...it, request_qty: Number(qty)||0 } : it);
      const next = { ...(prev||{}), items };
      scheduleAutoSave(next);
      return next;
    });
  };
  const updateIssueQty = (idx, qty) => {
    setSelectedRowIdx(idx);
    setData(prev => {
      const items = (prev?.items||[]).map((it,i)=> i===idx ? { ...it, to_issue_qty: Number(qty)||0 } : it);
      const next = { ...(prev||{}), items };
      scheduleAutoSave(next);
      return next;
    });
  };

  const removeItem = (idx) => {
    setData(prev => {
      const next = { ...(prev||{}), items: (prev?.items||[]).filter((_,i)=>i!==idx) };
      scheduleAutoSave(next);
      return next;
    });
  };

  const isWriteOffRequest = React.useMemo(() => {
    const raw = String(data?.requestType || (routeIsWriteOff ? 'writeoff' : 'purchase')).toLowerCase();
    return raw === 'writeoff';
  }, [data?.requestType, routeIsWriteOff]);
  const gdCanEditAwaiting = awaitingGd && (isGd || isAdmin || isSuper);
  const canEditManager = (canManagerStage && !managerSigned && !readOnly) || gdCanEditAwaiting;
  const canEditAssistant = (canAssistantStage && managerSigned && !assistantSigned && !statusDone && !data?.gdApprovalAt) || gdCanEditAwaiting; // заморозка позиций после отправки на утверждение
  const canEdit = !statusDone && (canEditManager || canEditAssistant || gdCanEditAwaiting);
  const canSignManagerNow = !managerSigned && canManagerStage && !statusDone;
  const canSignAssistantNow = managerSigned && !assistantSigned && !awaitingGd && canAssistantStage && !statusDone;
  const commentCanEdit = (canSignManagerNow || canSignAssistantNow || (awaitingGd && (canAssistantStage || isAdmin))) && !statusDone;
  const showReloadButton = !isWriteOffRequest && canEditManager && (data?.items?.length || 0) === 0 && !!(data?.cardCode || data?._cardInput);
  const showCreateTmcLink = canEditAssistant;
  const showAutoFillButtons = canEditManager && !isWriteOffRequest;

  const handleDraftNameChange = React.useCallback((value) => {
    setDraftItem((prev) => ({
      ...(prev || {}),
      name: value,
      tmc_id: null,
      unit: '',
      price: 0,
      supplier_link: '',
      group_id: null,
      card_qty: 0,
    }));
  }, []);

  const handleDraftPick = React.useCallback((item) => {
    if (!item) return;
    setDraftItem((prev) => ({
      ...(prev || {}),
      tmc_id: item.id ?? null,
      name: item.name || '',
      unit: item.unit || '',
      price: Number(item.price) || 0,
      supplier_link: item.supplier_link || '',
      group_id: item.group_id ?? item.groupId ?? null,
      card_qty: Number(item.card_qty ?? item.qty ?? 0) || 0,
    }));
  }, []);

  const commitDraftItem = React.useCallback(() => {
    if (!canEdit) return;
    if (!draftItem?.tmc_id) return;
    setData((prev) => {
      const base = Array.isArray(prev?.items) ? prev.items : [];
      const hydrated = {
        ...draftItem,
        request_qty: Number(draftItem.request_qty) || 0,
        to_issue_qty: Number(draftItem.to_issue_qty) || 0,
      };
      const next = { ...(prev || {}), items: [...base, hydrated] };
      scheduleAutoSave(next);
      return next;
    });
    setDraftItem(createEmptyRequestItem());
  }, [canEdit, draftItem, scheduleAutoSave]);

  const handleDraftBlur = React.useCallback((event) => {
    if (!canEdit) return;
    const host = draftRowRef.current;
    if (!host) return;
    const nextTarget = event?.relatedTarget;
    if (nextTarget && host.contains(nextTarget)) return;
    commitDraftItem();
  }, [canEdit, commitDraftItem]);

  const handleDraftQtyChange = (key, rawValue) => {
    setDraftItem((prev) => ({
      ...(prev || {}),
      [key]: rawValue,
    }));
  };

  const previousPeriod = React.useMemo(() => {
    const month = Number(data?.month);
    const year = Number(data?.year);
    if (!month || !year) return null;
    const pivot = new Date(year, month - 1, 1);
    pivot.setMonth(pivot.getMonth() - 1);
    return { month: pivot.getMonth() + 1, year: pivot.getFullYear() };
  }, [data?.month, data?.year]);
  const previousMonthLabel = previousPeriod ? `${FULL_MONTH_NAMES[(previousPeriod.month || 1) - 1] || previousPeriod.month} ${previousPeriod.year}` : '';

  const handleDraftKeyDown = React.useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraftItem();
    }
  }, [commitDraftItem]);

  const handleRowNameChange = React.useCallback((idx, value) => {
    if (!canEditManager) return;
    setSelectedRowIdx(idx);
    setData((prev) => {
      const items = (prev?.items || []).map((item, i) => {
        if (i !== idx) return item;
        return {
          ...item,
          name: value,
          tmc_id: null,
          unit: '',
          price: 0,
          supplier_link: '',
          group_id: null,
          card_qty: 0,
        };
      });
      const next = { ...(prev || {}), items };
      scheduleAutoSave(next, 500);
      return next;
    });
  }, [canEditManager, scheduleAutoSave]);

  const handleRowPick = React.useCallback((idx, item) => {
    if (!canEditManager || !item) return;
    setSelectedRowIdx(idx);
    setData((prev) => {
      const items = (prev?.items || []).map((row, i) => {
        if (i !== idx) return row;
        return {
          ...row,
          tmc_id: item.id ?? null,
          name: item.name || '',
          unit: item.unit || '',
          price: Number(item.price) || 0,
          supplier_link: item.supplier_link || '',
          group_id: item.group_id ?? item.groupId ?? null,
          card_qty: Number(item.card_qty ?? item.qty ?? row.card_qty ?? 0) || 0,
        };
      });
      const next = { ...(prev || {}), items };
      scheduleAutoSave(next, 300);
      return next;
    });
  }, [canEditManager, scheduleAutoSave]);

  const handleFillFromCalc = React.useCallback(() => {
    if (!canEditManager) return;
    setFillCalcLoading(true);
    setData((prev) => {
      const items = (prev?.items || []).map((item) => ({
        ...item,
        request_qty: Number(item.card_qty) || 0,
      }));
      const next = { ...(prev || {}), items };
      scheduleAutoSave(next, 300);
      return next;
    });
    setTimeout(() => setFillCalcLoading(false), 50);
  }, [canEditManager, scheduleAutoSave]);

  const applyPreviousMonthRows = React.useCallback((rows) => {
    const sourceRows = Array.isArray(rows) ? rows : [];
    const aggregated = new Map();
    sourceRows.forEach((row) => {
      const key = row?.tmc_id ? `id:${row.tmc_id}` : `name:${String(row?.name || '').toLowerCase()}`;
      const requestQty = Number(row?.copy_qty || row?.to_issue_qty || row?.request_qty) || 0;
      if (requestQty <= 0) return;
      const existing = aggregated.get(key);
      if (existing) {
        existing.requestQty += requestQty;
        return;
      }
      aggregated.set(key, {
        requestQty,
        template: {
          tmc_id: row?.tmc_id ?? null,
          name: row?.name || '',
          unit: row?.unit || '',
          price: Number(row?.price) || 0,
          supplier_link: row?.supplier_link || '',
          group_id: row?.group_id ?? null,
        },
      });
    });
    if (!aggregated.size) {
      alert('В выбранной карточке нет позиций для копирования.');
      return false;
    }
    setData((prev) => {
      const currentItems = Array.isArray(prev?.items) ? prev.items : [];
      const currentMap = new Map(currentItems.map((item) => {
        const key = item.tmc_id ? `id:${item.tmc_id}` : `name:${String(item.name || '').toLowerCase()}`;
        return [key, item];
      }));
      const nextItems = Array.from(aggregated.entries()).map(([key, info]) => {
        const existing = currentMap.get(key);
        if (existing) {
          return { ...existing, request_qty: info.requestQty };
        }
        const tmpl = info.template || {};
        return {
          tmc_id: tmpl.tmc_id ?? null,
          name: tmpl.name || '',
          unit: tmpl.unit || '',
          price: Number(tmpl.price) || 0,
          supplier_link: tmpl.supplier_link || '',
          group_id: tmpl.group_id ?? null,
          card_qty: 0,
          request_qty: info.requestQty,
          to_issue_qty: '',
        };
      });
      const next = { ...(prev || {}), items: nextItems };
      scheduleAutoSave(next, 300);
      return next;
    });
    return true;
  }, [scheduleAutoSave]);

  const closePrevMonthChoice = React.useCallback(() => {
    setPrevMonthChoice({ open: false, requests: [] });
  }, []);

  const handleFillFromPrevMonth = React.useCallback(async () => {
    if (!canEditManager || !previousPeriod || !data?.cardCode || fillPrevLoading) return;
    const targetCodeRaw = extractCardCode(data.cardCode) || String(data.cardCode || '').trim();
    const targetCode = targetCodeRaw ? targetCodeRaw.trim() : '';
    if (!targetCode) {
      alert('Не распознан код объекта. Сначала выберите актуальную расчётную карточку.');
      return;
    }
    setFillPrevLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('month', previousPeriod.month);
      params.set('year', previousPeriod.year);
      params.set('cardCode', targetCode);
      const resp = await fetchAuth(`${API}/api/tmc-requests/previous-items?${params.toString()}`);
      const payload = await resp.json();
      if (!resp.ok) {
        const message = payload?.error || 'load_failed';
        throw new Error(message);
      }
      const requests = Array.isArray(payload?.requests) ? payload.requests : [];
      if (!requests.length) {
        alert('Нет выполненных заявок в прошлом месяце по этому объекту.');
        return;
      }
      if (requests.length === 1) {
        applyPreviousMonthRows(requests[0].items || []);
        return;
      }
      setPrevMonthChoice({ open: true, requests });
    } catch (error) {
      console.error('fill prev month error', error);
      alert('Не удалось заполнить из предыдущего месяца.');
    } finally {
      setFillPrevLoading(false);
    }
  }, [applyPreviousMonthRows, canEditManager, data?.cardCode, fillPrevLoading, previousPeriod]);

  const handleClearAllItems = React.useCallback(() => {
    if (!canEditManager) return;
    if (!window.confirm('Удалить все позиции в заявке? Действие нельзя отменить.')) return;
    setData((prev) => {
      const next = { ...(prev || {}), items: [] };
      scheduleAutoSave(next, 300);
      return next;
    });
  }, [canEditManager, scheduleAutoSave]);

  const handleClearWriteOffColumn = React.useCallback(() => {
    if (!canEditManager || !isWriteOffRequest) return;
    setData((prev) => {
      const next = { ...(prev || {}), items: (prev?.items || []).map((it) => ({ ...it, to_issue_qty: 0 })) };
      scheduleAutoSave(next, 300);
      return next;
    });
  }, [canEditManager, isWriteOffRequest, scheduleAutoSave]);

  const requestedConsumablesSum = React.useMemo(() => {
    const dictById = new Map((tmcDict||[]).map(d => [String(d.id), d]));
    return (data?.items||[]).reduce((s, it) => {
      let gid = it.group_id;
      if (gid == null && it.tmc_id != null) { const d = dictById.get(String(it.tmc_id)); if (d && d.group_id != null) gid = d.group_id; }
      if (String(gid) === '1') s += (Number(it.request_qty)||0) * (Number(it.price)||0);
      return s;
    }, 0);
  }, [data, tmcDict]);

  const toIssueConsumablesSum = React.useMemo(() => {
    const dictById = new Map((tmcDict||[]).map(d => [String(d.id), d]));
    return (data?.items||[]).reduce((s, it) => {
      let gid = it.group_id;
      if (gid == null && it.tmc_id != null) { const d = dictById.get(String(it.tmc_id)); if (d && d.group_id != null) gid = d.group_id; }
      if (String(gid) === '1') s += (Number(it.to_issue_qty)||0) * (Number(it.price)||0);
      return s;
    }, 0);
  }, [data, tmcDict]);

  const toIssueAmortSum = React.useMemo(() => {
    const dictById = new Map((tmcDict||[]).map(d => [String(d.id), d]));
    return (data?.items||[]).reduce((s, it) => {
      let gid = it.group_id;
      if (gid == null && it.tmc_id != null) { const d = dictById.get(String(it.tmc_id)); if (d && d.group_id != null) gid = d.group_id; }
      if (String(gid) !== '1') s += (Number(it.to_issue_qty)||0) * (Number(it.price)||0);
      return s;
    }, 0);
  }, [data, tmcDict]);

  const writeOffStockTotal = React.useMemo(() => (data?.items || []).reduce((sum, it) => sum + ((Number(it.card_qty) || Number(it.request_qty) || 0) * (Number(it.price) || 0)), 0), [data?.items]);
  const writeOffIssueTotal = React.useMemo(() => (data?.items || []).reduce((sum, it) => sum + ((Number(it.to_issue_qty) || 0) * (Number(it.price) || 0)), 0), [data?.items]);

  // (requestedConsumablesSum уже вычисляется выше — используем его для блока сводки)

  const requestedAmortSum = React.useMemo(() => {
    const dictById = new Map((tmcDict||[]).map(d => [String(d.id), d]));
    return (data?.items||[]).reduce((s, it) => {
      let gid = it.group_id;
      if (gid == null && it.tmc_id != null) { const d = dictById.get(String(it.tmc_id)); if (d && d.group_id != null) gid = d.group_id; }
      if (String(gid) !== '1') s += (Number(it.request_qty)||0) * (Number(it.price)||0);
      return s;
    }, 0);
  }, [data, tmcDict]);

  const monthNames = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const filteredCards = React.useMemo(() => {
    const m = Number(data?.month)||0, y = Number(data?.year)||0;
    return (cards||[])
      .filter(c => Number(c?.periodMonth||0)===m && Number(c?.periodYear||0)===y && !!c?.actualAt)
      .filter(c => !!c?.objectCodeFull); // исключаем legacy без полного кода
  }, [cards, data?.month, data?.year]);

  // Отображение поля Код/объект: если пользователь сейчас вводит (_cardInput) — показываем ввод.
  // Иначе показываем "Имя (код)" если оба есть.
  const cardDisplay = React.useMemo(() => {
    if (data?._cardInput) return data._cardInput;
    const nm = data?.objectName || '';
    const code = data?.cardCode || '';
    return (nm && code) ? `${nm} (${code})` : (nm || code || '');
  }, [data?._cardInput, data?.objectName, data?.cardCode]);

  const desiredDateValue = React.useMemo(() => toInputDateString(data?.desiredDeliveryDate), [data?.desiredDeliveryDate]);
  const desiredDateDisplay = React.useMemo(() => formatDesiredDate(data?.desiredDeliveryDate), [data?.desiredDeliveryDate]);
  const displayRequestNumber = React.useMemo(() => {
    if (data?.id != null && String(data.id).trim() !== '') return String(data.id);
    if (data?.legacyId != null && String(data.legacyId).trim() !== '') return String(data.legacyId);
    return String(id || '').trim();
  }, [data?.id, data?.legacyId, id]);
  const filteredRequestItems = React.useMemo(() => {
    const source = Array.isArray(data?.items) ? data.items : [];
    const needle = String(itemFilters.name || '').trim().toLowerCase();
    const isNonZeroRow = (item) => {
      const requested = Number(item?.request_qty) || 0;
      const toIssue = Number(item?.to_issue_qty) || 0;
      return requested !== 0 || toIssue !== 0;
    };
    const dictById = new Map((Array.isArray(tmcDict) ? tmcDict : []).map((item) => [String(item.id), item]));
    return source
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => {
        const dictItem = item?.tmc_id != null ? dictById.get(String(item.tmc_id)) : null;
        const name = String(item?.name || '').toLowerCase();
        const code = String(dictItem?.code || '').toLowerCase();
        const unit = String(item?.unit || '').toLowerCase();
        const matchesText = !needle || name.includes(needle) || code.includes(needle) || unit.includes(needle);
        return matchesText && (!showOnlyNonZero || isNonZeroRow(item));
      });
  }, [data?.items, itemFilters.name, showOnlyNonZero, tmcDict]);

  const handleDesiredDateChange = React.useCallback((value) => {
    setData((prev) => {
      const next = { ...(prev || {}), desiredDeliveryDate: value || '' };
      scheduleAutoSave(next, 400);
      return next;
    });
  }, [scheduleAutoSave, setData]);

  function extractCardCode(input) {
    if (!input) return '';
    const paren = String(input).match(/\(([^)]+)\)\s*$/);
    if (paren && paren[1]) return paren[1].trim();
    const pat = String(input).match(/\b\d{3,4}-\d+\b/);
    if (pat && pat[0]) return pat[0];
    return String(input).trim();
  }

  const writeOffPrintRows = Array.isArray(data?.items)
    ? data.items
        .map((it, idx) => ({
          idx: idx + 1,
          name: it?.name || '—',
          unit: it?.unit || '—',
          price: Number(it?.price) || 0,
          stockQty: Number(it?.card_qty) || Number(it?.request_qty) || 0,
          issueQty: Number(it?.to_issue_qty) || 0,
          stockSum: ((Number(it?.card_qty) || Number(it?.request_qty) || 0) * (Number(it?.price) || 0)),
          issueSum: ((Number(it?.to_issue_qty) || 0) * (Number(it?.price) || 0)),
        }))
        .filter((row) => row.issueQty > 0)
    : [];

  const handleWriteOffPrint = () => {
    const objectName = data?.objectName || '—';
    const monthLabel = monthNames[(Number(data?.month) || 1) - 1] || '';
    const year = data?.year || '';
    const code = data?.cardCode || '—';
    const rowsHtml = writeOffPrintRows.length
      ? writeOffPrintRows.map((row) => `
          <tr>
            <td style="border:1px solid #d1d5db;padding:8px;text-align:center;">${row.idx}</td>
            <td style="border:1px solid #d1d5db;padding:8px;white-space:normal;word-break:break-word;">${row.name}</td>
            <td style="border:1px solid #d1d5db;padding:8px;text-align:center;">${row.unit}</td>
            <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${money(row.price)}</td>
            <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${formatQty(row.issueQty)}</td>
            <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${money(row.issueSum)}</td>
          </tr>
        `).join('')
      : '<tr><td colspan="6" style="border:1px solid #d1d5db;padding:12px;text-align:center;">Нет позиций</td></tr>';

    const printDoc = `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Накладная на списание ТМЦ</title>
          <style>
            html, body { margin: 0; padding: 0; background: #fff; font-family: Arial, sans-serif; color: #111827; }
            body { padding: 18mm 12mm; }
            .sheet { width: 100%; }
            h2 { margin: 0 0 16px; font-size: 30px; text-align: center; }
            .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-bottom: 14px; font-size: 15px; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f3f4f6; font-weight: 700; }
            .money { text-align: right; }
            .totals { margin-top: 10px; font-weight: 700; }
            .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 22px; font-size: 14px; }
            .line { border-bottom: 1px solid #111; min-height: 18px; display: block; margin-top: 6px; }
            @page { size: A4 portrait; margin: 10mm; }
          </style>
        </head>
        <body>
          <div class="sheet">
            <h2>Накладная на списание ТМЦ</h2>
            <div class="meta">
              <div><strong>Объект:</strong> ${objectName}</div>
              <div><strong>Месяц:</strong> ${monthLabel} ${year}</div>
              <div><strong>Код:</strong> ${code}</div>
              <div><strong>Дата:</strong> ${new Date().toLocaleDateString('ru-RU')}</div>
            </div>
            <table style="table-layout:fixed;">
              <thead>
                <tr>
                  <th style="width:40px;">№</th>
                  <th style="width:62%;">Наименование</th>
                  <th style="width:50px;">Ед.</th>
                  <th style="width:70px; text-align:right;">Цена</th>
                  <th style="width:80px; text-align:right;">Списано</th>
                  <th style="width:130px; text-align:right;">Сумма к списанию</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr>
                  <td colspan="5" style="border:1px solid #d1d5db;padding:8px;font-weight:700;">Итого</td>
                  <td class="money" style="border:1px solid #d1d5db;padding:8px;font-weight:700;">${money(writeOffIssueTotal)}</td>
                </tr>
              </tfoot>
            </table>
            <div class="signatures">
              <div><div>Подпись менеджера:</div><span class="line">${data?.createdByName || data?.managerSignedBy || ''}</span></div>
              <div><div>Подпись ответственного:</div><span class="line"></span></div>
            </div>
          </div>
        </body>
      </html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('style', 'position:fixed;width:0;height:0;left:-9999px;top:-9999px;');
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(printDoc);
    doc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch {}
    }, 1500);
  };

  return (
    <>
      <div className="tmc-request-page">
      <div className="tmc-request-toolbar">
        <Link to="/tmc-requests">&larr; К журналу</Link>
        <h2 style={{ display:'flex', alignItems:'center', gap:12 }}>
          {isNew
            ? (isWriteOffRequest ? 'Новая заявка на списание ТМЦ' : 'Новая заявка ТМЦ')
            : (isWriteOffRequest ? `Заявка на списание ТМЦ № ${displayRequestNumber}` : `Заявка ТМЦ № ${displayRequestNumber}`)}
          {data && <StatusBadge data={data} />}
        </h2>
        <div className="tmc-request-toolbar-actions">
          {canEdit && !data?.id && (
            <button onClick={saveDraft} disabled={isLoading} style={{ padding:'6px 12px' }}>Сохранить</button>
          )}
          {isWriteOffRequest && data?.id && (
            <button onClick={handleWriteOffPrint} style={{ padding:'6px 12px', background:'#f8fafc', border:'1px solid #64748b', borderRadius:6 }}>Печать накладной</button>
          )}
          {!readOnly && (canSignManagerNow || canSignAssistantNow) && (
            <button onClick={sign} disabled={isLoading || !data?.id} style={{ padding:'6px 12px', background:'#e7f6d4', border:'1px solid #8bc34a', borderRadius:6 }}>
              Подписать {(!managerSigned ? '(менеджер)' : '(помощник ГД)')}
            </button>
          )}
          {(assistantSigned || awaitingGd) && (statusDone || awaitingGd) && !data?.gdSignedAt && canAssistantStage && canAssistantUnsignNow && (
            <button onClick={async()=>{
              if (!data?.id) return;
              setIsLoading(true);
              try {
                const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/unsign/assistant`, { method:'POST' });
                if (r.ok) {
                  const payload = await r.json();
                  applyServerData(payload);
                } else {
                  const body = await r.json().catch(()=>({}));
                  if (body?.error === 'assistant_unsign_window_expired') alert('Срок для снятия подписи помощника истёк (2 рабочих дня).');
                  else alert('Не удалось снять подпись помощника');
                }
              } finally { setIsLoading(false); }
            }} style={{ padding:'6px 12px', background:'#fff3cd', border:'1px solid #e6b800', borderRadius:6 }}>Снять подпись (помощник ГД)</button>
          )}
          {data?.gdApprovalAt && !data?.gdSignedAt && !statusDone && (isAdmin || isSuper) && (
            <>
              <button onClick={async()=>{
                if (!data?.id) return;
                setIsLoading(true);
                try {
                  const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/sign/gd`, { method:'POST' });
                  if (r.ok) {
                    const payload = await r.json();
                    applyServerData(payload);
                  } else alert('Не удалось подписать (ГД)');
                } finally { setIsLoading(false); }
              }} style={{ padding:'6px 12px', background:'#d4e7f6', border:'1px solid #4a8bc3', borderRadius:6 }}>Подписать (ГД)</button>
              <button onClick={async()=>{
                if (!data?.id) return;
                setIsLoading(true);
                try {
                  const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/gd-return`, { method:'POST' });
                  if (r.ok) {
                    const payload = await r.json();
                    applyServerData(payload);
                  } else alert('Не удалось отправить на доработку');
                } finally { setIsLoading(false); }
              }} style={{ padding:'6px 12px', background:'#fde68a', border:'1px solid #d97706', borderRadius:6 }}>Отправить на доработку</button>
            </>
          )}
          {data?.gdSignedAt && statusDone && isAdmin && (
            <button onClick={async()=>{
              if (!data?.id) return;
              setIsLoading(true);
              try {
                const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/unsign/gd`, { method:'POST' });
                if (r.ok) {
                  const payload = await r.json();
                  applyServerData(payload);
                } else alert('Не удалось снять подпись ГД');
              } finally { setIsLoading(false); }
            }} style={{ padding:'6px 12px', background:'#fff3cd', border:'1px solid #d97706', borderRadius:6 }}>Снять подпись (ГД)</button>
          )}
          {managerSigned && !assistantSigned && !statusDone && !awaitingGd && canManagerStage && (
            <button onClick={async()=>{
              if (!data?.id) return;
              setIsLoading(true);
              try {
                const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/unsign/manager`, { method:'POST' });
                if (r.ok) {
                  const payload = await r.json();
                  applyServerData(payload);
                } else alert('Не удалось снять подпись менеджера');
              } finally { setIsLoading(false); }
            }} style={{ padding:'6px 12px', background:'#fff3cd', border:'1px solid #e6b800', borderRadius:6 }}>Снять подпись (менеджер)</button>
          )}
        </div>
      </div>

      {prevMonthChoice.open && createPortal(
        <div
          onClick={closePrevMonthChoice}
          style={{ position:'fixed', inset:0, background:'rgba(15, 23, 42, 0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width:'min(720px, 100%)', background:'#fff', borderRadius:14, boxShadow:'0 24px 80px rgba(15, 23, 42, 0.28)', border:'1px solid #dbe2ea', overflow:'hidden' }}
          >
            <div style={{ padding:'18px 20px 12px', borderBottom:'1px solid #e5e7eb' }}>
              <div style={{ fontSize:18, fontWeight:700, color:'#0f172a' }}>Из какой карточки копировать?</div>
              <div style={{ marginTop:6, color:'#475569', fontSize:13 }}>
                В прошлом месяце найдено {prevMonthChoice.requests.length} заявки. Выберите карточку, чтобы скопировать строки.
              </div>
            </div>
            <div style={{ padding:20, display:'grid', gap:10, maxHeight:'50vh', overflow:'auto' }}>
              {prevMonthChoice.requests.map((request) => (
                <button
                  key={request.id}
                  onClick={() => {
                    const ok = applyPreviousMonthRows(request.items || []);
                    if (ok) closePrevMonthChoice();
                  }}
                  style={{
                    width:'100%',
                    textAlign:'left',
                    padding:'12px 14px',
                    border:'1px solid #cbd5e1',
                    borderRadius:12,
                    background:'#f8fafc',
                    color:'#0f172a',
                    cursor:'pointer',
                  }}
                >
                  <div style={{ fontWeight:700 }}>Карточка № {request.id}</div>
                  <div style={{ marginTop:4, fontSize:13, color:'#475569' }}>Сумма заказа: {money(request.orderSum)} ₽</div>
                </button>
              ))}
            </div>
            <div style={{ padding:'0 20px 20px', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={closePrevMonthChoice} style={{ padding:'8px 14px', border:'1px solid #cbd5e1', borderRadius:8, background:'#fff' }}>Отмена</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {isLoading ? (<div style={{ textAlign:'center' }}>Загрузка…</div>) : error ? (<div style={{ textAlign:'center', color:'#c00' }}>{error}</div>) : (
        <div style={{ display:'grid', gap:16 }}>
          <div className="tmc-request-meta">
            <label>
              <span style={{ color:'#444' }}>Месяц (мес):</span>
              {!managerSigned ? (
                <select value={data?.month||''} onChange={e=>{ if (canEditManager) { setData(prev=>{ const next = {...(prev||{}), month: Number(e.target.value)}; scheduleAutoSave(next); return next; }); } }} style={{ padding:'8px 12px', border:'1px solid #c6c8d6', borderRadius:8, background:'#fff' }}>
                  {Array.from({length:12},(_,i)=>i+1).map(m=>(<option key={m} value={m}>{monthNames[m-1]}</option>))}
                </select>
              ) : (
                <div style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, background:'#f9fafb' }}>{monthNames[(Number(data?.month)||1)-1] || ''}</div>
              )}
            </label>
            <label>
              <span style={{ color:'#444' }}>Год:</span>
              <input type="number" value={data?.year||''} onChange={e=>{ if (canEditManager) { setData(prev=>{ const next = {...(prev||{}), year: Number(e.target.value)}; scheduleAutoSave(next); return next; }); } }} style={{ width:130, padding:'8px 12px', border:'1px solid #c6c8d6', borderRadius:8, background: data?.id ? '#f9fafb' : '#fff' }} readOnly={!!data?.id} />
            </label>
            <div className="tmc-request-object-row">
              <div className="tmc-request-field-flex">
                <span style={{ color:'#444', marginBottom:4, display:'block' }}>Код/объект:</span>
                {!managerSigned ? (
                  <div style={{ width:'100%' }}>
                    <input
                      list="card-list"
                      value={cardDisplay}
                      onChange={e=>{ if (canEditManager) setData(prev=>{ const txt = e.target.value; const match = txt.match(/\b\d{3,4}-\d+\b/); return { ...(prev||{}), _cardInput: txt, cardCode: match ? match[0] : (prev?.cardCode||''), objectName: prev?.objectName || '' }; }); }}
                      onBlur={e=>{ if (canEditManager && e.target.value) { const code = extractCardCode(e.target.value); if (/^\d{3,4}-\d+$/.test(code)) { pickCard(code); } } }}
                      placeholder="Введите название объекта или код (напр. 0001-23)"
                      className="tmc-request-card-input"
                    />
                    <datalist id="card-list">
                      {(filteredCards||[]).map(c => {
                        const full = c.objectCodeFull || c.objectCode;
                        const label = `${c.objectName} (${full})`;
                        return (
                          <option key={full} value={label}>{label}</option>
                        );
                      })}
                    </datalist>
                  </div>
                ) : (
                  <div className="tmc-request-card-display">
                    {cardDisplay || '—'}
                  </div>
                )}
              </div>
              {!isWriteOffRequest && (
                <div className="tmc-request-field-flex tmc-request-date-field">
                  <span style={{ color:'#444', marginBottom:4, display:'block' }}>Желаемая дата завоза:</span>
                  {canEditManager ? (
                    <input
                      type="date"
                      className="tmc-request-card-input"
                      value={desiredDateValue}
                      onChange={(e) => handleDesiredDateChange(e.target.value)}
                    />
                  ) : (
                    <div className="tmc-request-card-display">
                      {desiredDateDisplay || '—'}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Убрали старые метки лимитов вне таблицы: информация отображается в сводной таблице ниже */}
          </div>
          {/* Информация о лимитах и заказанных суммах (табличный вид с подсветкой) */}
          {!isWriteOffRequest && (() => {
            const prevCons = Number(data?.limitInfo?.prev?.consumables)||0;
            const prevAmort = Number(data?.limitInfo?.prev?.amortization)||0;
            // Этап-зависимые суммы и подписи
            const onAssistantStage = !!managerSigned && !assistantSigned;
            const afterAssistantStage = !!assistantSigned || statusDone;
            const currCons = afterAssistantStage
              ? Number(toIssueConsumablesSum)||0
              : onAssistantStage
              ? Number(toIssueConsumablesSum)||0
              : Number(requestedConsumablesSum)||0;
            const currAmort = afterAssistantStage
              ? Number(toIssueAmortSum)||0
              : onAssistantStage
              ? Number(toIssueAmortSum)||0
              : Number(requestedAmortSum)||0;
            const limCons = Number(data?.limitConsumables)||0;
            const limAmort = Number(data?.limitAmortMonth)||0;
            const totCons = prevCons + currCons;
            const totAmort = prevAmort + currAmort;
            const consOk = limCons <= 0 ? true : (totCons <= limCons + 1e-6);
            const amortOk = limAmort <= 0 ? true : (totAmort <= limAmort + 1e-6);
            const cell = (align='right') => ({ padding:'6px 10px', border:'1px solid #e5e7eb', textAlign:align });
            const th = (txt) => (<th style={{ padding:'6px 10px', border:'1px solid #e5e7eb', background:'#f9fafb', textAlign:'left' }}>{txt}</th>);
            return (
              <div className="tmc-request-summary">
                <table style={{ borderCollapse:'separate', borderSpacing:0, border:'1px solid #e5e7eb', width:'100%' }}>
                  <thead>
                    <tr>
                      {th('Показатель')}
                      {th('Расходники')}
                      {th('Амортизация')}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={cell('left')}>Лимит</td>
                      <td style={{ ...cell(), background: consOk ? '#e7f6d4' : '#fde2e2', fontWeight:700 }}>{money(limCons)} ₽</td>
                      <td style={{ ...cell(), background: amortOk ? '#e7f6d4' : '#fde2e2', fontWeight:700 }}>{money(limAmort)} ₽/мес</td>
                    </tr>
                    <tr>
                      <td style={cell('left')}>Заказано ранее</td>
                      <td style={cell()}>{money(prevCons)} ₽</td>
                      <td style={cell()}>{money(prevAmort)} ₽</td>
                    </tr>
                    <tr>
                      <td style={cell('left')}>{!managerSigned ? 'В заявке заказано менеджером' : 'К выдаче по заявке'}</td>
                      <td style={cell()}>{money(currCons)} ₽</td>
                      <td style={cell()}>{money(currAmort)} ₽</td>
                    </tr>
                    <tr>
                      <td style={{ ...cell('left'), fontWeight:700 }}>Итого</td>
                      <td style={{ ...cell(), fontWeight:700 }}>{money(totCons)} ₽</td>
                      <td style={{ ...cell(), fontWeight:700 }}>{money(totAmort)} ₽</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

          <div style={{ border:'1px solid #e5e7eb', borderRadius:8 }} className="tmc-request-table-wrapper">
            <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, tableLayout:'auto' }} className="tmc-items-table">
              <thead>
                <tr style={{ background:'#f5f5f5' }}>
                  <th style={{ padding:8, textAlign:'right', width:46 }}>№</th>
                  <th style={{ padding:8, textAlign:'left', width:'34%', minWidth:360 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                      <span>Наименование</span>
                      {(showReloadButton || (canEditManager && (data?.items?.length || 0) > 0 && !readOnly)) && (
                        <div style={{ display:'flex', gap:8 }}>
                          {showReloadButton && (
                            <button
                              onClick={async()=>{
                                if (!data?.id) return;
                                setIsLoading(true);
                                try {
                                  const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}/reload-items`, { method:'POST' });
                                  if (r.ok) {
                                    const upd = await r.json();
                                    applyServerData(upd);
                                  } else {
                                    alert('Не удалось загрузить позиции из расчета');
                                  }
                                } finally {
                                  setIsLoading(false);
                                }
                              }}
                              style={{ padding:'4px 10px', border:'1px solid #4caf50', borderRadius:6, background:'#e8f5e9', color:'#1b5e20', fontSize:12, fontWeight:600 }}
                            >
                              Загрузить из расчета
                            </button>
                          )}
                          {canEditManager && (data?.items?.length || 0) > 0 && !readOnly && (
                            <button
                              onClick={handleClearAllItems}
                              style={{ padding:'4px 10px', border:'1px solid #ef4444', borderRadius:6, background:'#fee2e2', color:'#b91c1c', fontSize:12, fontWeight:600 }}
                            >
                              Очистить все
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8, flexWrap:'wrap' }}>
                      <input
                        value={itemFilters.name}
                        onChange={(e) => setItemFilters((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Поиск позиции..."
                        style={{ flex:'1 1 220px', minWidth:180, padding:'6px 8px', fontSize:13, borderRadius:8, border:'1px solid #cfd4e1', boxSizing:'border-box' }}
                      />
                      <button
                        onClick={() => setShowOnlyNonZero((prev) => !prev)}
                        title="Показывать только строки, где есть количество"
                        aria-pressed={showOnlyNonZero}
                        style={{
                          padding:'6px 10px',
                          border:'1px solid',
                          borderColor: showOnlyNonZero ? '#0f766e' : '#cfd4e1',
                          borderRadius:8,
                          background: showOnlyNonZero ? '#ccfbf1' : '#fff',
                          color: showOnlyNonZero ? '#115e59' : '#334155',
                          fontSize:12,
                          fontWeight:600,
                          whiteSpace:'nowrap',
                        }}
                      >
                        {showOnlyNonZero ? 'Показаны только ненулевые' : 'Только ненулевые'}
                      </button>
                    </div>
                  </th>
                  <th style={{ padding:8, textAlign:'center', width:90 }}>Ед.</th>
                  <th style={{ padding:8, textAlign:'right', width:120 }}>Цена</th>
                  <th style={{ padding:8, textAlign:'right', width:140 }}>{isWriteOffRequest ? 'Остаток на складе' : 'Кол-во в расчете'}</th>
                  <th style={{ padding:8, textAlign:'right', width:180 }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                      <span>{isWriteOffRequest ? 'Количество к списанию' : 'Заказанное кол-во'}</span>
                      {isWriteOffRequest && canEditManager && (
                        <button
                          onClick={handleClearWriteOffColumn}
                          style={{ padding:'4px 8px', border:'1px solid #ef4444', borderRadius:6, background:'#fee2e2', color:'#b91c1c', fontSize:12, width:'100%' }}
                        >
                          Очистить
                        </button>
                      )}
                      {!isWriteOffRequest && showAutoFillButtons && (
                        <>
                          <button
                            onClick={handleFillFromCalc}
                            disabled={fillCalcLoading}
                            style={{ padding:'4px 8px', border:'1px solid #a78bfa', borderRadius:6, background:'#f5f3ff', color:'#5b21b6', fontSize:12, width:'100%' }}
                          >
                            {fillCalcLoading ? 'Заполняем…' : 'Добавить из расчёта'}
                          </button>
                          <button
                            onClick={handleFillFromPrevMonth}
                            disabled={fillPrevLoading || !previousPeriod || !data?.cardCode}
                            style={{ padding:'4px 8px', border:'1px solid #0ea5e9', borderRadius:6, background:'#ecfeff', color:'#0369a1', fontSize:12, width:'100%' }}
                          >
                            {fillPrevLoading ? 'Собираем данные…' : `Из прошлого месяца (${previousMonthLabel || 'нет данных'})`}
                          </button>
                        </>
                      )}
                    </div>
                  </th>
                  {!isWriteOffRequest && (
                    <th style={{ padding:8, textAlign:'right', width:150 }}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                        <span>К выдаче кол-во</span>
                        {canEditAssistant && (
                          <button
                            onClick={()=>{
                              setData(prev => {
                                const items = (prev?.items||[]).map(it => ({ ...it, to_issue_qty: Number(it.request_qty)||0 }));
                                const next = { ...(prev||{}), items };
                                scheduleAutoSave(next, 300);
                                return next;
                              });
                            }}
                            title="Заполнить по заказанному"
                            style={{ padding:'4px 8px', border:'1px solid #1976d2', borderRadius:6, background:'#e3f2fd', color:'#0d47a1', fontSize:12, width:'100%' }}
                          >Заполнить</button>
                        )}
                      </div>
                    </th>
                  )}
                  <th style={{ padding:8, textAlign:'right', width:140 }}>{isWriteOffRequest ? 'Сумма на складе' : 'Сумма заказа'}</th>
                  <th style={{ padding:8, textAlign:'right', width:140 }}>{isWriteOffRequest ? 'Сумма к списанию' : 'Сумма к выдаче'}</th>
                  <th style={{ padding:8, textAlign:'center', width:60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRequestItems.map(({ item: it, idx }) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedRowIdx(idx)}
                    style={{
                      background: selectedRowIdx === idx ? '#e0f2fe' : (idx%2===0 ? '#fff' : '#fafafa'),
                      borderLeft: selectedRowIdx === idx ? '3px solid #93c5fd' : undefined,
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <td className="tmc-col-num" style={{ padding:8, textAlign:'right', color:'#475569', fontVariantNumeric:'tabular-nums' }}>{idx + 1}</td>
                    <td className="tmc-col-name" style={{ padding:8, minWidth:360 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1 }}>
                          {canEditManager ? (
                            <InlineTmcPicker
                              value={it.name}
                              dict={tmcDict}
                              disabled={!canEditManager}
                              onInputChange={(value) => handleRowNameChange(idx, value)}
                              onPick={(item) => handleRowPick(idx, item)}
                              placeholder="Выберите позицию или начните вводить"
                            />
                          ) : (
                            <span>{it.name || '—'}</span>
                          )}
                        </div>
                        {it.tmc_id ? (
                          <a href={`${window.location.origin}/tmc/${it.tmc_id}`} target="_blank" rel="noopener noreferrer" title="Открыть карточку ТМЦ" style={{ textDecoration:'none' }}>↗</a>
                        ) : null}
                        {it.supplier_link ? (
                          <a href={/^https?:\/\//i.test(it.supplier_link) ? it.supplier_link : `http://${it.supplier_link}`} target="_blank" rel="noopener noreferrer" title="Ссылка из карточки ТМЦ" style={{ textDecoration:'none', marginLeft:8, color:'#3a27c5' }}>Ссылка</a>
                        ) : null}
                      </div>
                    </td>
                    <td className="tmc-col-unit" data-label="Ед." style={{ padding:8, textAlign:'center' }}>{it.unit}</td>
                    <td className="tmc-col-price" data-label="Цена" style={{ padding:8, textAlign:'right' }}>{money(it.price)}</td>
                    <td className="tmc-col-calc-qty" data-label={isWriteOffRequest ? 'Остаток на складе' : 'Кол-во в расчете'} style={{ padding:8, textAlign:'right' }}>{formatQty(isWriteOffRequest ? (Number(it.card_qty) || Number(it.request_qty) || 0) : it.card_qty)}</td>
                    <td className="tmc-col-req-qty" data-label={isWriteOffRequest ? 'Количество к списанию' : 'Заказанное кол-во'} style={{ padding:8, textAlign:'right' }}>
                      {isWriteOffRequest ? (
                        <input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={it.to_issue_qty ?? ''}
                          onFocus={() => setSelectedRowIdx(idx)}
                          onChange={e => canEditManager && updateIssueQty(idx, e.target.value)}
                          style={{ width:120, padding:'6px 8px', border:'1px solid #ccc', borderRadius:6, textAlign:'right', background: !canEditManager ? '#f3f4f6' : '#fff', color: !canEditManager ? '#555' : '#000' }}
                          readOnly={!canEditManager}
                        />
                      ) : (
                        <input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={it.request_qty ?? ''}
                          onFocus={() => setSelectedRowIdx(idx)}
                          onChange={e => canEditManager && updateItemQty(idx, e.target.value)}
                          style={{ width:104, padding:'6px 8px', border:'1px solid #ccc', borderRadius:6, textAlign:'right', background: !canEditManager ? '#f3f4f6' : '#fff', color: !canEditManager ? '#555' : '#000' }}
                          readOnly={!canEditManager}
                        />
                      )}
                    </td>
                    {!isWriteOffRequest && (
                      <td className="tmc-col-issue-qty" data-label="К выдаче кол-во" style={{ padding:8, textAlign:'right' }}>
                        <input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={it.to_issue_qty ?? ''}
                          onFocus={() => setSelectedRowIdx(idx)}
                          onChange={e => canEditAssistant && updateIssueQty(idx, e.target.value)}
                          style={{ width:104, padding:'6px 8px', border:'1px solid #ccc', borderRadius:6, textAlign:'right', background: !canEditAssistant ? '#f3f4f6' : '#fff', color: !canEditAssistant ? '#555' : '#000' }}
                          readOnly={!canEditAssistant}
                        />
                      </td>
                    )}
                    <td className="tmc-col-req-sum" data-label={isWriteOffRequest ? 'Сумма на складе' : 'Сумма заказа'} style={{ padding:8, textAlign:'right' }}>{money((isWriteOffRequest ? (Number(it.card_qty) || Number(it.request_qty) || 0) : (Number(it.request_qty)||0)) * (Number(it.price)||0))}</td>
                    <td className="tmc-col-issue-sum" data-label={isWriteOffRequest ? 'Сумма к списанию' : 'Сумма к выдаче'} style={{ padding:8, textAlign:'right' }}>{money((Number(it.to_issue_qty)||0) * (Number(it.price)||0))}</td>
                    <td className="tmc-col-actions" style={{ padding:8, textAlign:'center' }}>
                      {canEdit && <button onClick={()=>removeItem(idx)} title="Удалить" style={{ border:'none', background:'none', color:'#c00', cursor:'pointer' }}>🗑️</button>}
                    </td>
                  </tr>
                ))}
                {canEdit && (
                  <tr ref={draftRowRef} onBlurCapture={handleDraftBlur} style={{ background:'#fdfcff' }}>
                    <td className="tmc-col-num" style={{ padding:8, textAlign:'right', color:'#94a3b8', fontVariantNumeric:'tabular-nums' }}>{(data?.items?.length || 0) + 1}</td>
                    <td className="tmc-col-name" style={{ padding:8, minWidth:360 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <InlineTmcPicker
                            value={draftItem.name}
                            dict={tmcDict}
                            disabled={!canEdit}
                            onInputChange={handleDraftNameChange}
                            onPick={handleDraftPick}
                            placeholder="Начните вводить новую позицию или код"
                          />
                        </div>
                        {draftItem.tmc_id ? (
                          <a
                            href={`${window.location.origin}/tmc/${draftItem.tmc_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration:'none' }}
                          >↗</a>
                        ) : null}
                      </div>
                    </td>
                    <td className="tmc-col-unit" data-label="Ед." style={{ padding:8, textAlign:'center', color:'#94a3b8' }}>{draftItem.unit || '—'}</td>
                    <td className="tmc-col-price" data-label="Цена" style={{ padding:8, textAlign:'right', color:'#94a3b8' }}>{draftItem.tmc_id ? money(draftItem.price) : '—'}</td>
                    <td className="tmc-col-calc-qty" data-label={isWriteOffRequest ? 'Остаток на складе' : 'Кол-во в расчете'} style={{ padding:8, textAlign:'right', color:'#94a3b8' }}>{draftItem.card_qty ? formatQty(draftItem.card_qty) : '—'}</td>
                    <td className="tmc-col-req-qty" data-label={isWriteOffRequest ? 'Количество к списанию' : 'Заказанное кол-во'} style={{ padding:8, textAlign:'right' }}>
                      <input
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={draftItem.to_issue_qty ?? (isWriteOffRequest ? draftItem.request_qty ?? '' : '')}
                        onChange={(e) => { if (canEditManager || canEditAssistant) handleDraftQtyChange('to_issue_qty', e.target.value); }}
                        onKeyDown={handleDraftKeyDown}
                        style={{ width:120, padding:'6px 8px', border:'1px dashed #c4b5fd', borderRadius:6, textAlign:'right', background: !(canEditManager || canEditAssistant) ? '#f3f4f6' : '#fff' }}
                        readOnly={!(canEditManager || canEditAssistant)}
                      />
                    </td>
                    {!isWriteOffRequest && (
                      <td className="tmc-col-issue-qty" data-label="К выдаче кол-во" style={{ padding:8, textAlign:'right' }}>
                        <input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={draftItem.to_issue_qty ?? ''}
                          onChange={(e) => { if (canEditAssistant) handleDraftQtyChange('to_issue_qty', e.target.value); }}
                          onKeyDown={handleDraftKeyDown}
                          style={{ width:104, padding:'6px 8px', border:'1px dashed #c4b5fd', borderRadius:6, textAlign:'right', background: !canEditAssistant ? '#f3f4f6' : '#fff' }}
                          readOnly={!canEditAssistant}
                        />
                      </td>
                    )}
                    <td className="tmc-col-req-sum" data-label={isWriteOffRequest ? 'Сумма на складе' : 'Сумма заказа'} style={{ padding:8, textAlign:'right', color:'#94a3b8' }}>{draftItem.tmc_id ? money(((isWriteOffRequest ? (Number(draftItem.card_qty) || Number(draftItem.request_qty) || 0) : (Number(draftItem.request_qty)||0)) * (Number(draftItem.price)||0))) : '—'}</td>
                    <td className="tmc-col-issue-sum" data-label={isWriteOffRequest ? 'Сумма к списанию' : 'Сумма к выдаче'} style={{ padding:8, textAlign:'right', color:'#94a3b8' }}>{draftItem.tmc_id ? money((Number(draftItem.to_issue_qty)||0) * (Number(draftItem.price)||0)) : '—'}</td>
                    <td className="tmc-col-actions" style={{ padding:8, textAlign:'center', color:'#cbd5f5' }}>—</td>
                  </tr>
                )}
                {(data?.items||[]).length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding:14, textAlign:'center', color:'#555', background:'#fffbea', borderTop:'1px solid #f3e8a8' }}>
                      В расчете на выбранный месяц ТМЦ не предусмотрено
                    </td>
                  </tr>
                )}
                {(data?.items||[]).length > 0 && filteredRequestItems.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding:14, textAlign:'center', color:'#64748b', background:'#fff' }}>
                      По указанному фильтру позиции не найдены
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                {isWriteOffRequest ? (
                  <tr>
                    <td colSpan={6} style={{ padding:8, fontWeight:700, borderTop:'1px solid #e5e7eb' }}>ИТОГО:</td>
                    <td style={{ padding:8, textAlign:'right', fontWeight:800, borderTop:'1px solid #e5e7eb' }}>{money(writeOffStockTotal)}</td>
                    <td style={{ padding:8, textAlign:'right', fontWeight:800, borderTop:'1px solid #e5e7eb' }}>{money(writeOffIssueTotal)}</td>
                    <td style={{ borderTop:'1px solid #e5e7eb' }}></td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={6} style={{ padding:8, fontWeight:700 }}>ИТОГО (расходники):</td>
                    <td style={{ padding:8, textAlign:'right', fontWeight:800 }}>{money(requestedConsumablesSum)}</td>
                    <td style={{ padding:8, textAlign:'right', fontWeight:800 }}>{money(toIssueConsumablesSum)}</td>
                    <td></td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>

          {showCreateTmcLink && (
            <div style={{ display:'flex' }}>
              <a href="/tmc/new" target="_blank" rel="noopener noreferrer" style={{ padding:'8px 12px', border:'1px solid #c59bff', borderRadius:8, background:'#fff', color:'#5427b0', fontWeight:700, textDecoration:'none' }}>
                Создать ТМЦ ↗
              </a>
            </div>
          )}

          {/* Комментарии: только чтение + добавление через отдельную кнопку */}
          <CommentsBlock data={data} setData={applyServerData} canEdit={commentCanEdit} />

          {(data?.managerSignedAt || data?.assistantSignedAt || data?.gdApprovalAt || data?.gdSignedAt || (Array.isArray(data?.comments) && data.comments.length)) && (
            <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, background:'#f8fafc' }}>
              <div style={{ fontWeight:700, marginBottom:8 }}>Подписи</div>
              <div style={{ display:'grid', gap:8 }}>
                {data?.managerSignedAt && (
                  <div style={{ padding:10, background:'#e7f6d4', border:'1px solid #8bc34a', borderRadius:8 }}>
                    <div><b>Менеджер:</b> {data.managerSignedBy || data.createdByName || '—'} — {new Date(data.managerSignedAt).toLocaleString('ru-RU')}</div>
                    {(() => { const c = (Array.isArray(data?.comments)?data.comments:[]).filter(x=>x.role==='manager').slice(-1)[0];
                      return c ? (<div style={{ marginTop:4, fontSize:13, color:'#2f4f2f' }}>Комментарий: {c.text} — {c.name || ''} ({new Date(c.at).toLocaleString('ru-RU')})</div>) : null; })()}
                  </div>
                )}
                {data?.assistantSignedAt && (
                  <div style={{ padding:10, background:'#d4e7f6', border:'1px solid #4a8bc3', borderRadius:8 }}>
                    <div><b>Помощник ГД:</b> {data.assistantSignedBy || '—'} — {new Date(data.assistantSignedAt).toLocaleString('ru-RU')}</div>
                    {(() => { const c = (Array.isArray(data?.comments)?data.comments:[]).filter(x=>x.role==='assistant').slice(-1)[0];
                      return c ? (<div style={{ marginTop:4, fontSize:13, color:'#1b4368' }}>Комментарий: {c.text} — {c.name || ''} ({new Date(c.at).toLocaleString('ru-RU')})</div>) : null; })()}
                  </div>
                )}
                {data?.gdApprovalAt && !data?.gdSignedAt && (
                  <div style={{ padding:10, background:'#fffbea', border:'1px solid #f59e0b', borderRadius:8 }}>
                    <div><b>На утверждении у ГД:</b> {data.gdApprovalBy || '—'} — {new Date(data.gdApprovalAt).toLocaleString('ru-RU')}</div>
                  </div>
                )}
                {data?.gdSignedAt && (
                  <div style={{ padding:10, background:'#e7f6d4', border:'1px solid #8bc34a', borderRadius:8 }}>
                    <div><b>Ген. директор:</b> {data.gdSignedBy || '—'} — {new Date(data.gdSignedAt).toLocaleString('ru-RU')}</div>
                    {(() => { const c = (Array.isArray(data?.comments)?data.comments:[]).filter(x=>x.role==='gd' || x.role==='admin').slice(-1)[0];
                      return c ? (<div style={{ marginTop:4, fontSize:13, color:'#2f4f2f' }}>Комментарий: {c.text} — {c.name || ''} ({new Date(c.at).toLocaleString('ru-RU')})</div>) : null; })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
      {isWriteOffRequest && (
        <div className="writeoff-print-sheet" style={{ display: 'none', padding: 24 }}>
          <h2 style={{ margin:'0 0 16px' }}>Накладная на списание ТМЦ</h2>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div><strong>Объект:</strong> {data?.objectName || '—'}</div>
            <div><strong>Месяц:</strong> {monthNames[(Number(data?.month) || 1) - 1] || ''} {data?.year || ''}</div>
            <div><strong>Код:</strong> {data?.cardCode || '—'}</div>
            <div><strong>Дата:</strong> {new Date().toLocaleDateString('ru-RU')}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th className="num" style={{ width:40 }}>№</th>
                <th>Наименование</th>
                <th style={{ width:70 }}>Ед.</th>
                <th className="money" style={{ width:120 }}>Цена</th>
                <th className="money" style={{ width:120 }}>Списано</th>
                <th className="money" style={{ width:160 }}>Сумма к списанию</th>
              </tr>
            </thead>
            <tbody>
              {writeOffPrintRows.map((row) => (
                <tr key={row.idx}>
                  <td className="num">{row.idx}</td>
                  <td>{row.name}</td>
                  <td>{row.unit}</td>
                  <td className="money">{money(row.price)}</td>
                  <td className="money">{formatQty(row.issueQty)}</td>
                  <td className="money">{money(row.issueSum)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} style={{ fontWeight:700 }}>Итого</td>
                <td className="money" style={{ fontWeight:700 }}>{money(writeOffIssueTotal)}</td>
              </tr>
            </tfoot>
          </table>
          <div style={{ marginTop:20, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <strong>Подпись менеджера:</strong> {data?.createdByName || data?.managerSignedBy || '—'} ____________________
            </div>
            <div><strong>Подпись ответственного:</strong> ____________________</div>
          </div>
        </div>
      )}
    </>
  );
}

function InlineTmcPicker({ value, dict, onInputChange, onPick, disabled, placeholder }) {
  const anchorRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [dropdownStyle, setDropdownStyle] = React.useState({ top: 0, left: 0, width: 0, maxHeight: 320 });
  const searchNeedle = React.useMemo(() => String(value || '').trim().toLowerCase(), [value]);
  const filtered = React.useMemo(() => {
    const base = Array.isArray(dict) ? dict : [];
    if (!searchNeedle) return base.slice(0, 300);
    return base
      .filter((item) => {
        const name = String(item?.name || '').toLowerCase();
        const code = String(item?.code || '').toLowerCase();
        return name.includes(searchNeedle) || code.includes(searchNeedle);
      })
      .slice(0, 300);
  }, [dict, searchNeedle]);

  const syncInputHeight = React.useCallback(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = 'auto';
    const nextHeight = Math.min(120, Math.max(36, inputRef.current.scrollHeight));
    inputRef.current.style.height = `${nextHeight}px`;
  }, []);

  React.useEffect(() => {
    syncInputHeight();
  }, [value, syncInputHeight]);

  const positionDropdown = React.useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const margin = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minWidth = Math.min(720, Math.max(420, viewportWidth - margin * 2));
    const desiredWidth = Math.min(Math.max(rect.width, minWidth), viewportWidth - margin * 2);
    const left = Math.max(margin, Math.min(rect.left, viewportWidth - desiredWidth - margin));
    const maxHeight = Math.min(420, Math.max(240, viewportHeight - margin * 2));
    const availableBottom = viewportHeight - rect.bottom - margin;
    const availableTop = rect.top - margin;
    const openUp = availableBottom < 260 && availableTop > availableBottom;
    const rawTop = openUp ? rect.top - maxHeight - 8 : rect.bottom + 8;
    const top = Math.max(margin, Math.min(rawTop, viewportHeight - margin - 120));
    setDropdownStyle({ top, left, width: desiredWidth, maxHeight });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    positionDropdown();
    const handle = () => positionDropdown();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [open, positionDropdown]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    positionDropdown();
    syncInputHeight();
  };

  const handleChange = (event) => {
    if (disabled) return;
    onInputChange(event.target.value);
    setOpen(true);
    syncInputHeight();
  };

  const dropdown = !disabled && open
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            zIndex: 2000,
            top: dropdownStyle.top,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            maxHeight: dropdownStyle.maxHeight,
            overflowY: 'auto',
            border: '1px solid #cbd5f5',
            borderRadius: 12,
            background: '#fff',
            boxShadow: '0 25px 60px rgba(15,23,42,0.25)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 13, color: '#94a3b8' }}>Ничего не найдено</div>
          ) : (
            filtered.map((item) => (
              <button
                type="button"
                key={item.id}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onPick(item);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.name || 'Без названия'}</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                  Код: {item.code || '—'} · Ед.: {item.unit || '—'} · Цена: {money(item.price)}
                </div>
                {item.supplier_link && (
                  <div style={{ fontSize: 11, color: '#6366f1', marginTop: 2, wordBreak: 'break-all' }}>{item.supplier_link}</div>
                )}
              </button>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div style={{ width: '100%' }}>
      <textarea
        ref={(node) => {
          anchorRef.current = node;
          inputRef.current = node;
        }}
        rows={1}
        value={value || ''}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            event.currentTarget.blur();
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
        placeholder={disabled ? 'Добавление недоступно' : (placeholder || 'Начните вводить название или код')}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px dashed #c4b5fd',
          borderRadius: 8,
          fontSize: 13,
          lineHeight: 1.35,
          background: disabled ? '#f3f4f6' : '#fff',
          resize: 'none',
          overflowY: 'hidden',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      />
      {dropdown}
    </div>
  );
}

// Встроенный блок комментариев: одна строка комментария с историей, формат dd.mm.yyyy hh:mm: Фамилия И.О.: текст
function CommentsBlock({ data, setData, canEdit }) {
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const API = 'http://localhost:3001';
  function withAuthHeaders(headers = {}) {
    const token = localStorage.getItem('authToken') || '';
    return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
  }
  async function fetchAuth(url, options = {}) {
    const opts = { ...options, headers: withAuthHeaders(options.headers || {}) };
    return fetch(url, opts);
  }

  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser') || 'null') || {};
    } catch {
      return {};
    }
  }, []);
  const currentUserName = String(currentUser?.name || '').trim() || 'Сотрудник';

  const formatCommentTimestamp = (date = new Date()) => {
    const d = new Date(date);
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const parseCommentLines = (raw) => {
    const lines = typeof raw === 'string' ? raw.split(/\r?\n/) : Array.isArray(raw) ? raw : [];
    return lines
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/^([0-9]{2}\.[0-9]{2}\.[0-9]{4}\s+[0-9]{2}:[0-9]{2}):\s*(.+?):\s*(.*)$/);
        if (!match) return { raw: line, author: '', text: line, when: null };
        const [, when, author, text] = match;
        return { raw: line, author: author.trim(), text: text.trim(), when: when.trim() };
      });
  };

  const commentEntries = React.useMemo(() => {
    const source = typeof data?.comment === 'string' ? data.comment : (Array.isArray(data?.comments) ? data.comments.map((c) => c?.text || '').join('\n') : '');
    return parseCommentLines(source);
  }, [data?.comment, data?.comments]);

  const ownEntry = React.useMemo(() => {
    const matches = commentEntries.filter((entry) => entry.author === currentUserName);
    return matches[matches.length - 1] || null;
  }, [commentEntries, currentUserName]);

  const openEditor = () => {
    const nextDraft = ownEntry ? ownEntry.text : '';
    setDraft(nextDraft);
    setAdding(true);
  };

  const saveComment = async () => {
    if (!data?.id || !draft.trim()) return;
    try {
      const currentText = typeof data?.comment === 'string' ? data.comment : (Array.isArray(data?.comments) ? data.comments.map((c) => c?.text || '').join('\n') : '');
      const existingLines = parseCommentLines(currentText).map((entry) => entry.raw);
      const timestamp = formatCommentTimestamp();
      const newLine = `${timestamp}: ${currentUserName}: ${draft.trim()}`;
      const ownIndex = parseCommentLines(currentText).findLastIndex((entry) => entry.author === currentUserName);
      const nextLines = Array.isArray(existingLines) && existingLines.length ? existingLines.slice() : [];
      if (ownIndex >= 0) nextLines[ownIndex] = newLine;
      else nextLines.push(newLine);
      const commentValue = nextLines.join('\n');

      const r = await fetchAuth(`${API}/api/tmc-requests/${data.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: commentValue })
      });
      if (r.ok) {
        const upd = await r.json();
        setData(upd);
        setDraft('');
        setAdding(false);
      } else {
        const body = await r.json().catch(()=>({}));
        if (body?.error === 'forbidden') alert('Нет прав для добавления комментария на текущей стадии.');
        else if (body?.error === 'already_fully_signed') alert('Заявка уже полностью подписана. Комментарии недоступны.');
        else alert('Не удалось сохранить комментарий.');
      }
    } catch {
      alert('Не удалось сохранить комментарий.');
    }
  };

  return (
    <div style={{ display:'grid', gap:8 }}>
      <div style={{ fontWeight:700 }}>Комментарий</div>
      <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:10, maxHeight:220, overflow:'auto', background:'#fafafa', whiteSpace:'pre-wrap', lineHeight:1.5 }}>
        {commentEntries.length === 0 ? (
          <div style={{ color:'#666' }}>Комментариев пока нет.</div>
        ) : (
          commentEntries.map((entry, i) => (
            <div key={`${entry.raw}-${i}`} style={{ padding:'4px 0', borderBottom: i < commentEntries.length - 1 ? '1px solid #eee' : 'none' }}>
              {entry.author ? (
                <div>{entry.when ? <span style={{ color:'#64748b' }}>{entry.when} </span> : null}<b>{entry.author}</b>: {entry.text}</div>
              ) : (
                <div>{entry.when ? <span style={{ color:'#64748b' }}>{entry.when} </span> : null}{entry.text}</div>
              )}
            </div>
          ))
        )}
      </div>

      {canEdit && !adding && (
        <div>
          <button onClick={openEditor} style={{ padding:'6px 10px', border:'1px solid #4a8bc3', borderRadius:6, background:'#d4e7f6', color:'#1b4368' }}>Изменить комментарий</button>
        </div>
      )}

      {canEdit && adding && (
        <div style={{ display:'grid', gap:8 }}>
          <textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={3} placeholder="Введите комментарий…" style={{ width:'100%', padding:'8px 10px', border:'1px solid #c6c8d6', borderRadius:8 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={saveComment} style={{ padding:'6px 10px', border:'1px solid #8bc34a', borderRadius:6, background:'#e7f6d4', color:'#2f4f2f' }}>Сохранить</button>
            <button onClick={()=>{ setDraft(''); setAdding(false); }} style={{ padding:'6px 10px' }}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

function roleName(code) {
  switch (code) {
    case 'manager': return 'менеджер';
    case 'assistant': return 'помощник ГД';
    case 'gd': return 'ГД';
    case 'admin': return 'администратор';
    default: return code || '';
  }
}

// Статус-бейдж
function StatusBadge({ data }) {
  // Фоллбэк вычисления статуса, если нет поля status (или хотим перепроверить)
  let st = String(data?.status || '').trim();
  if (!st) {
    if (data?.gdSignedAt) st = 'done';
    else if (data?.gdApprovalAt) st = 'awaiting_gd';
    else if (data?.assistantSignedAt) st = 'done';
    else if (data?.managerSignedAt) st = 'in_progress';
    else st = 'draft';
  }
  const map = {
    draft: { text: 'Черновик', bg: '#f3f4f6', color:'#374151', border:'#d1d5db' },
    in_progress: { text: 'В обработке', bg: '#e0f2fe', color:'#0369a1', border:'#7dd3fc' },
    awaiting_gd: { text: 'На утверждении у ГД', bg: '#fef9c3', color:'#b45309', border:'#fde68a' },
    done: { text: 'Выполнено', bg: '#dcfce7', color:'#065f46', border:'#86efac' }
  };
  const m = map[st] || map.draft;
  return (
    <span title={`Статус: ${m.text}`} style={{
      display:'inline-flex',
      alignItems:'center',
      padding:'4px 10px',
      fontSize:13,
      fontWeight:600,
      border:`1px solid ${m.border}`,
      borderRadius:20,
      background:m.bg,
      color:m.color,
      lineHeight:1
    }}>{m.text}</span>
  );
}
